/*
 * DISPUTED IDENTITIES — the open questions the traditions never settled, made a place to argue.
 *
 * Is the son Abraham was told to sacrifice Isaac or Ishmael? Is the Quran's Idris the Bible's Enoch?
 * Is Mary confused with Miriam? The gazetteer records the disagreement and refuses to resolve it —
 * so this panel does the same: each figure is an open question with the case laid out, and two ways
 * in — see the figure on the map, or weigh in on it. It is the map's invitation to dialogue: the
 * points where people actually disagree, offered as somewhere to disagree together, never a verdict.
 */
import { useLang } from '../i18n/lang';
import type { Strings } from '../i18n/strings';

export interface DisputedItem {
  id: string;
  name: string;
  /** 'contested' — genuinely disputed identity — or 'same-name-only' — same name, likely two people. */
  agreement: string;
  note: string;
  /** Whether this figure is a node on the map — only then can the reader be sent to it. */
  onMap: boolean;
}

/**
 * A passage more than one tradition's channels were measured arguing over.
 *
 * The counterpart to a disputed FIGURE, one level down: an argument starts at a verse someone
 * put on screen, not at an entity.
 *
 * `channels` RANKS this list and is never shown. It is how many separate sources reached for the
 * passage — the honest weight, since one source repeating a verse is not eight sources each citing
 * it — but that is a fact about how the library was measured, and a reader is owed the finding
 * rather than the instrument. Sorting by it is the use it was built for; printing it was not.
 */
export interface ContestedPassageItem {
  ref: string;
  /** 'christian' | 'muslim' | 'jewish', as measured. Always two or more. */
  traditions: readonly string[];
  channels: number;
  /** The first verse, which is where opening it lands the reader. Absent for a hadith. */
  firstId: number | undefined;
  /*
   * The figure whose panel carries this, when it is a hadith rather than scripture.
   *
   * A report is cited as collection-and-number — "Bukhari 6130" — and has no verse to land on,
   * so opening it sends the reader to the figure it is about, where its text already lives.
   * Exactly one of `firstId` and `node` is set; the build drops anything with neither.
   */
  node?: string;
  /** Passages heard beside this one. Empty where nothing was measured. */
  alongside?: readonly { ref: string; channels: number; firstId: number }[];
}

export interface DisputedPanelProps {
  items: DisputedItem[];
  /** Passages, ranked by how many channels contested them. Empty when the bundle predates them. */
  passages?: ContestedPassageItem[];
  /** Send the reader to the figure on the map. */
  onOpen: (id: string) => void;
  /** Open the passage: the reader for scripture, the figure's panel for a hadith. */
  onOpenPassage?: (firstId: number | undefined, ref: string, node?: string) => void;
  /** Open the discussion for this question, to weigh in. */
  onDebate: (id: string, name: string) => void;
  onClose: () => void;
}

function badgeKey(agreement: string): keyof Strings {
  return agreement === 'same-name-only' ? 'disputed.badgeSameName' : 'disputed.badgeDisputed';
}

/* Spelled out rather than built from the tradition id, so a tradition the dictionary does not
   know yet shows its own name instead of a missing-key blank. */
const TRADITION_KEY: Readonly<Record<string, keyof Strings>> = {
  christian: 'contested.tradition.christian',
  muslim: 'contested.tradition.muslim',
  jewish: 'contested.tradition.jewish',
};

export function DisputedPanel({ items, passages = [], onOpen, onOpenPassage, onDebate, onClose }: DisputedPanelProps): React.JSX.Element {
  const { t } = useLang();
  return (
    <aside className="tellings" role="region" aria-label={t('contested.open')}>
      <div className="tellings-head">
        <h2 className="tellings-title">{t('contested.open')}</h2>
        <button type="button" className="tellings-close" onClick={onClose} aria-label={t('action.close')}>×</button>
      </div>
      <div className="tellings-body">
        {/*
          * PASSAGES LEAD, and that ordering was decided by looking at the panel rather than by
          * reasoning about it. The disputed figures are a curated list of sixty-one; putting them
          * first pushed the passages — the ranked, measured half, and the part a reader can act on
          * immediately — a full screen below the fold. Whichever list is longest should not decide
          * what is seen first.
          */}
        {passages.length > 0 && (
          <>
            <h3 className="disputed-subheading">{t('contested.passagesHeading')}</h3>
            <p className="tellings-lead">{t('contested.passagesLead')}</p>
            <ul className="difference-list">
              {passages.map(passage => (
                <li key={passage.ref} className="difference-item">
                  <button
                    type="button"
                    className="contested-passage"
                    onClick={() => onOpenPassage?.(passage.firstId, passage.ref, passage.node)}
                  >
                    <span className="contested-ref">{passage.ref}</span>
                    {/* The count that used to sit here named the study's own apparatus — see the
                        note on `channels` above. It still ORDERS this list, which is the honest
                        use of a measurement; the traditions beside each row are the evidence a
                        reader gets, and they are general. */}
                    <span className="contested-traditions">
                      {passage.traditions.map(tradition => {
                        const key = TRADITION_KEY[tradition];
                        return (
                          <span key={tradition} className={`contested-tradition contested-tradition--${tradition}`}>
                            {key ? t(key) : tradition}
                          </span>
                        );
                      })}
                    </span>
                  </button>
                  {/*
                    * What the passage travels with — a second row of targets, each one another
                    * passage to open. Kept visibly lighter than the passage above it because it
                    * is weaker evidence: two verses arriving together is measured, but whether
                    * that is an argument or a speaker reading on is not something the corpus
                    * knows. "Argued alongside" is as far as the data reaches.
                    */}
                  {passage.alongside && passage.alongside.length > 0 && (
                    <div className="contested-alongside">
                      <span className="contested-alongside-label">{t('contested.alongside')}</span>
                      {passage.alongside.map(companion => (
                        <button
                          key={companion.ref}
                          type="button"
                          className="contested-companion"
                          onClick={() => onOpenPassage?.(companion.firstId, companion.ref)}
                        >
                          {companion.ref}
                        </button>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}

        <h3 className="disputed-subheading">{t('disputed.heading')}</h3>
        <p className="tellings-lead">{t('disputed.lead')}</p>
        <ul className="difference-list">
          {items.map(item => (
            <li key={item.id} className="difference-item">
              <p className="difference-context">
                <span className="disputed-name">{item.name}</span>
                <span className={`disputed-badge disputed-badge--${item.agreement}`}>{t(badgeKey(item.agreement))}</span>
              </p>
              <p className="disputed-note">{item.note}</p>
              <div className="disputed-actions">
                <button type="button" className="difference-open" onClick={() => onDebate(item.id, item.name)}>
                  {t('disputed.weighIn')}
                </button>
                {item.onMap && (
                  <button type="button" className="disputed-explore" onClick={() => onOpen(item.id)}>
                    {t('disputed.seeOnMap', { name: item.name })}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>

      </div>
    </aside>
  );
}
